
import { useState } from 'react';
import { ExternalLink, Globe, Layout, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { webApps, webAppCategories } from '@/data/portfolio';
import { TiltCard } from '@/components/ui/TiltCard';

// Category icon colors for visual distinction
const categoryColors: Record<string, string> = {
    "FinTech": "from-emerald-500 to-teal-500",
    "Finance & FinTech": "from-emerald-500 to-teal-500",
    "FinTech & Investment": "from-emerald-500 to-teal-500",
    "AI & Productivity": "from-purple-600 to-indigo-600",
    "E-commerce": "from-orange-500 to-amber-500",
    "SaaS": "from-blue-600 to-cyan-600",
    "SaaS & Legal Tech": "from-blue-600 to-cyan-600",
    "Legal Tech": "from-indigo-500 to-blue-500",
    "All": "from-primary to-primary/80"
};

const getGradient = (category: string, badgeColor?: string) => {
    if (badgeColor) {
        switch (badgeColor.toLowerCase()) {
            case 'purple': return "from-purple-600 to-indigo-600";
            case 'green': return "from-emerald-500 to-teal-500";
            case 'orange': return "from-orange-500 to-amber-500";
            case 'blue': return "from-blue-600 to-cyan-600";
            default: return categoryColors[category] || "from-primary to-primary/80";
        }
    }
    return categoryColors[category] || "from-primary to-primary/80";
};

export function WebAppsSection() {
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [visibleCount, setVisibleCount] = useState(6);

    const filteredApps = selectedCategory === "All"
        ? webApps
        : webApps.filter(app => {
            // Handle combined categories (e.g. "SaaS & Legal Tech" should match "SaaS" and "Legal Tech")
            return app.category.includes(selectedCategory) ||
                (selectedCategory === "FinTech" && app.category.includes("Finance"));
        });

    const displayedApps = filteredApps.slice(0, visibleCount);

    const handleVisitClick = (appName: string) => {
        console.log(`[Analytics] Visit Site click: ${appName}`);
    };

    return (
        <section className="py-20 bg-background relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Monitor className="w-5 h-5 text-primary" />
                        <span className="text-primary font-semibold tracking-wider uppercase text-sm">
                            Web Platforms
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                        Web Applications Portfolio
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Explore our collection of live web applications, SaaS platforms, and enterprise solutions built with modern tech stacks
                    </p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {webAppCategories.map((category) => (
                        <Button
                            key={category}
                            variant={selectedCategory === category ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                                setSelectedCategory(category);
                                setVisibleCount(6);
                            }}
                            className="rounded-full"
                        >
                            {category}
                        </Button>
                    ))}
                </div>

                {/* Apps Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {displayedApps.map((app) => {
                        const gradientClass = getGradient(app.category, app.badgeColor);

                        return (
                            <div key={app.name} className="block h-full">
                                <TiltCard className="group glass-card-hover rounded-xl p-6 transition-all duration-300 h-full flex flex-col items-stretch relative border border-border/50 bg-card/50 backdrop-blur-sm">
                                    <a
                                        href={app.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => handleVisitClick(app.name)}
                                        className="block flex-1 group"
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-4">
                                            <div className="flex items-start gap-4 flex-1 min-w-0">
                                                {/* App Icon with gradient and initial */}
                                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradientClass}
                                      flex items-center justify-center flex-shrink-0 shadow-lg
                                      group-hover:scale-110 transition-transform duration-300`}>
                                                    <span className="text-white font-bold text-xl">
                                                        {app.iconInitial}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0 pt-1">
                                                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate pr-2 text-lg">
                                                        {app.name}
                                                    </h3>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium inline-block mt-1 truncate max-w-full`}>
                                                        {app.category}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-sm text-muted-foreground mb-6 line-clamp-2 h-10">
                                            {app.description}
                                        </p>

                                        {/* Tech Stack Pills */}
                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {app.technologies?.slice(0, 4).map((tech, i) => (
                                                <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                                                    {tech}
                                                </span>
                                            ))}
                                            {app.technologies && app.technologies.length > 4 && (
                                                <span className="text-[10px] px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                                                    +{app.technologies.length - 4}
                                                </span>
                                            )}
                                        </div>
                                    </a>

                                    {!app.hideVisitCta && (
                                        <div className="mt-auto pt-4 border-t border-border/50">
                                            <a
                                                href={app.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => handleVisitClick(app.name)}
                                                className="w-full inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg group-hover:translate-y-[-2px]"
                                            >
                                                <Globe className="w-4 h-4" />
                                                <span>Visit Site</span>
                                                <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-70" />
                                            </a>
                                        </div>
                                    )}
                                </TiltCard>
                            </div>
                        );
                    })}
                </div>

                {/* Load More */}
                {visibleCount < filteredApps.length && (
                    <div className="text-center mt-12">
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => setVisibleCount(prev => prev + 6)}
                            className="rounded-full px-8"
                        >
                            Load More Applications
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
